// Where a v2 bulk draft stands, read off its ingestion job (build doc 06 §7.2,
// §7.5). Pure functions: the flow places itself with them, and the converting
// view draws its progress from the same job.
//
//   | Job status                     | Resume step                              |
//   |--------------------------------|------------------------------------------|
//   | OPEN                           | Add responses (DRAG_DROP)                |
//   | EXPLODING / READING / GROUPING | Converting                               |
//   | FAILED                         | Converting (shows the failure)           |
//   | READY / COMMITTING             | Organise (COMMITTING re-sends the commit)|
//   | COMMITTED                      | the submission-based post-commit resume  |
//   | CLOSED / DISCARDED             | none: the resolver decides (a new job)   |

export const V2_STEP = Object.freeze({
  DRAG_DROP: 'dragDrop',
  CONVERTING: 'converting',
  ORGANISE: 'organise',
  REVIEW: 'review',
});

/** What resumeStepFor returns for a committed job: the caller's post-commit logic decides. */
export const COMMITTED = 'committed';

export const CONVERTING_STATUSES = Object.freeze([
  'EXPLODING',
  'READING',
  'GROUPING',
]);
export const CHECKING_STATUSES = Object.freeze(['READY', 'COMMITTING']);

export const isConverting = (status) => CONVERTING_STATUSES.includes(status);
export const isChecking = (status) => CHECKING_STATUSES.includes(status);

/** The wizard step for a job status (table above); null when the job is no longer this draft's. */
export const resumeStepFor = (status) => {
  if (status === 'OPEN') return V2_STEP.DRAG_DROP;
  if (isConverting(status) || status === 'FAILED') return V2_STEP.CONVERTING;
  if (isChecking(status)) return V2_STEP.ORGANISE;
  if (status === 'COMMITTED') return COMMITTED;
  return null;
};

const RESPONSE = 'RESPONSE';

/**
 * studentId → the ingestion page ids committed for them, in script order
 * (RESPONSE pages only): from the job's own `commit.students[].pageIds` when
 * the job carries them, else from a commit reply's students[] and the
 * workspace scripts.
 */
export const committedPageIdsByStudent = ({ commit, students, ws } = {}) => {
  const out = {};
  const fromJob = Array.isArray(commit?.students) ? commit.students : null;
  if (fromJob && fromJob.some((entry) => Array.isArray(entry?.pageIds))) {
    fromJob.forEach((entry) => {
      if (entry?.studentId && Array.isArray(entry.pageIds))
        out[String(entry.studentId)] = [...entry.pageIds];
    });
    return out;
  }
  const list = Array.isArray(students) ? students : fromJob || [];
  list.forEach((entry) => {
    const script = entry?.scriptId ? ws?.scripts?.[entry.scriptId] : null;
    if (!entry?.studentId || !script) return;
    out[String(entry.studentId)] = (script.pageIds || []).filter(
      (pageId) => (ws.pages?.[pageId]?.kind ?? RESPONSE) === RESPONSE
    );
  });
  return out;
};

/** The submission ids a committed job holds (`commit.submissionIds`), else []. */
export const committedSubmissionIds = (commit) =>
  Array.isArray(commit?.submissionIds)
    ? commit.submissionIds.filter(Boolean)
    : [];

// Stage keys of the shared ConvertingView (01 §5.4 conversion.stages).
const STAGES = Object.freeze([
  'READING_FILES',
  'DETECTING_HANDWRITING',
  'CONVERTING_TO_TEXT',
  'MATCHING_SCRIPTS',
]);

const stageIndexFor = (status) => {
  if (status === 'READING') return 2;
  if (status === 'GROUPING' || isChecking(status) || status === 'COMMITTED')
    return 3;
  return 0;
};

/**
 * The ConvertingView `conversion` block for a bulk job, which has no exam
 * status poll: pages read out of pages found, and the stage from the status.
 */
export const conversionFor = (ws) => {
  const counts = ws?.counts || {};
  const total = Number(counts.pages) || 0;
  return {
    stages: STAGES,
    stageIndex: stageIndexFor(ws?.status),
    pagesTotal: total,
    pagesRead: Math.min(
      total,
      (Number(counts.pagesRead) || 0) + (Number(counts.pagesFailed) || 0)
    ),
    nowReading: null,
  };
};

/** The pages whose read failed, as ConvertingView failures ("We couldn't read page {n} of {file}"). */
export const failuresFor = (ws) =>
  Object.values(ws?.pages || {})
    .filter((page) => page?.readStatus === 'FAILED')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((page) => ({
      kind: 'CONVERSION',
      pageId: page.pageId,
      fileName: page.fileName || null,
      pageNumber: page.pageNumber || null,
    }));
